/**
 * TMR Default API Endpoints.
 *
 * Brandings can replace endpoints by redeclaring this constant. Endpoints can also be be
 * added/changed/removed with {@link TmrApi}.
 *
 * By convention, endpoint names that map directly to a {@link TmrResource} are capitalized
 * and have the same base name of the resource service. This allows components to magically
 * make the right query.
 *
 * @namespace tmrDefaultApiEndpoints
 * @type {ngConstant}
 */
export const constant = [
  'tmrDefaultApiEndpoints',
  {
    Alert: 'alerts/:id',
    'Alert.current': 'alerts/current',
    'Antenna.free': 'rfidAntennas/free',
    'Antenna.start': { path: 'rfidAntennas/:id/start', deviceManager: true },
    'Antenna.start.websocket': {
      protocol: 'ws',
      path: 'rfidAntennas/scannings',
      deviceManager: true,
    },
    'Antenna.stop': { path: 'rfidAntennas/:id/stop', deviceManager: true },
    'Antenna.read': { path: 'rfidAntennas/:id/read', deviceManager: true },
    'Antenna.write': { path: 'rfidAntennas/:id/write', deviceManager: true },
    'Antenna.kill': { path: 'rfidAntennas/:id/kill', deviceManager: true },
    Antenna: 'rfidAntennas/:id',
    AppData: 'appDatas/:id',
    Asn: 'asns/:id',
    BarcodeReader: 'barcodeReaders/:id',
    Capability: 'capabilities/:id',
    changePassword: 'auth/changePassword',
    checkPassword: 'auth/checkPassword',
    Checklist: 'checklists/:id',
    ChecklistByItem: 'checklistsByItem/:id',
    ChecklistByUpc: 'checklistsByUpc/:id',
    Configuration: 'configurations/:id',
    'Configuration.reload': 'configurations/reload/all',
    Conveyor: 'conveyors/:id',
    'Conveyor.start': 'conveyors/:id/start',
    'Conveyor.status': 'conveyors/:id/status',
    'Conveyor.stop': 'conveyors/:id/stop',
    ConveyorPackageEvent: 'conveyorPackageEvents/:id',
    ConveyorPackageScanning: 'conveyorPackageScannings/:id',
    CycleCount: 'cycleCounts/:id',
    'CycleCount.items': 'cycleCounts/:id/items',
    'CycleCount.counters': 'cycleCounts/:id/counters',
    'CycleCountItem.aggregated': 'cycleCountItems/aggregated/:id',
    'CycleCountItem.aggregatedCount': 'cycleCountItems/aggregated/:id/count',
    'CycleCountItem.aggregatedExport':
      'cycleCountItems/aggregated/:id/export/csv',
    CycleCountItem: 'cycleCountItems/:id',
    DeviceController: 'deviceControllers/:id',
    'DeviceController.apiKey': 'deviceControllers/:id/apiKey',
    'epc.encode': 'epc/encode',
    'epc.decode': 'epc/decode',
    EventLog: 'eventLogs/:id',
    Hierarchy: 'hierarchies/:id',
    'Inventory.counters': 'inventories/:id/counters',
    Inventory: 'inventories/:id',
    InventoryItem: 'inventories/:id/items',
    InventoryPlaceMovement: 'inventoryplaceMovements',
    InventoryZoneMovement: 'inventoryZoneMovements',
    'Item.aggregate': 'items/aggregate',
    'Item.aggregateCount': 'items/aggregate/count',
    'Item.aggregateExport': 'items/aggregate/export',
    'Item.bulkDecode': 'items/decode',
    'Item.decode': 'items/decode',
    'Item.encode': 'items/encoding',
    Item: 'items/:id',
    ItemConfiguration: 'itemConfigurations/:id',
    loggedUser: 'auth/loggedUser',
    login: 'auth/login',
    loginAs: 'auth/loginAs',
    logout: 'auth/logout',
    'Movement.items': 'movements/:id/items',
    'Movement.itemsCount': 'movements/:id/items/count',
    'Movement.itemsExport': 'movements/:id/items/export',
    Movement: 'movements/:id',
    MovementEncoding: 'encodings/:id',
    MovementInbound: 'inbounds/:id',
    'MovementInbound.status': 'inbounds/status',
    MovementOutbound: 'outbounds/:id',
    'MovementOutbound.status': 'outbounds/status',
    MovementReturn: 'returns/:id',
    'MovementReturn.status': 'returns/status',
    MovementSale: 'sales/:id',
    'MovementSale.status': 'sales/status',
    MovementShippingInbound: 'shipping/report/inbound/:id',
    MovementShippingOutbound: 'shipping/report/outbound/:id',
    MovementInventoryConfirmation: 'inventoryConfirmations/:id',
    MovementTransferToZone: 'transfersToZone/:id',
    OperationConfiguration: 'operationConfigurations/:id',
    'MovementTransferToZone.status': 'transfersToZone/status',
    'Parcel.adjustment': 'parcels/:id/adjustment',
    'Parcel.adjustmentDetail': 'parcels/:id/adjustmentDetail',
    'Parcel.findForInbound': 'parcels/findForInbound',
    'Parcel.items': 'parcels/:id/parcelItems',
    Parcel: 'parcels/:id',
    passwordOptions: 'auth/passwordRules',
    ping: 'ping',
    'Place.return': 'places/:id/return',
    'Place.sell': 'places/:id/sell',
    'Place.zones': 'places/:id/zones',
    Place: 'places/:id',
    'PlaceInventory.confirm': 'placeInventories/:id/confirm',
    PlaceInventory: 'placeInventories',
    Printer: 'printers/:id',
    'Printer.start': 'printers/:id/start',
    'Printer.status': 'printers/:id/status',
    'Printer.stop': 'printers/:id/stop',
    PrintRequest: 'printRequests/:id',
    PrintSession: 'printSessions/:id',
    'PrintSession.cancel': 'printSessions/:id/cancel',
    'PrintSession.bulkCancel': 'printSessions/bulkCancel',
    'Product.createProductGroup': 'products/createproductgroup',
    'Product.distinctKeys': 'products/distinctKeys',
    'Product.distinctValues': 'products/distinctValues',
    'Product.keys': 'products/keys',
    'Product.nextSerial': 'products/:upc/nextSerial',
    'Product.print': 'products/printer/:id/sessionCreate',
    'Product.printBulk': 'products/printer/bulkSessionCreate',
    'Product.printable': 'products/printer',
    'Product.printableCount': 'products/printer/count',
    'Product.reindex': 'products/reindex',
    'Product.values': 'products/values',
    Product: 'products/:id',
    'Product.image': 'products/image/:sku/full/full/0/default.jpg',
    'Product.attributeSuggestion': 'products/attributesuggestion',
    ProductGroup: 'productgroups/:id',
    ProductPrintSession: 'productPrintSessions/:id',
    ProductionLot: 'productionLots/:id',
    'ProductionLot.print': 'productionLots/printer/:id/sessionCreate',
    'ProductionLot.printBulk': 'productionLots/printer/bulkSessionCreate',
    'ProductionLot.printable': 'productionLots/printer',
    'ProductionLot.printableCount': 'productionLots/printer/count',
    'ProductionLot.updateProgress': 'productionLots/updateProgress',
    ProductionLotPrintSession: 'productionLotPrintSessions/:id',
    ProductionOrder: 'productionOrders/:id',
    'ProductionOrderRow.print': 'productionOrderRows/printer/:id/sessionCreate',
    'ProductionOrderRow.printBulk':
      'productionOrderRows/printer/bulkSessionCreate',
    'ProductionOrderRow.printable': 'productionOrderRows/printer',
    'ProductionOrderRow.printableCount': 'productionOrderRows/printer/count',
    ProductionOrderRow: 'productionOrderRows/:id',
    ProductionOrderRowPrintSession: 'productionOrderRowPrintSessions/:id',
    ProductionWorkflow: 'productionworkflows/:id',
    'serial.nextValue': 'serials/nextValue',
    Workstation: 'workstations/:id',
    ReceiveParcelTransfer: 'receiveParcels',
    resetPassword: 'auth/resetPassword',
    resetPasswordAdmin: 'auth/resetPasswordAdmin',
    Return: 'returns/:id',
    RfidGate: 'rfidgates/:id',
    RfidReader: 'rfidreaders/:id',
    Role: 'roles/:id',
    'Role.capabilities': 'roles/capabilities',
    Sales: 'sales',
    Scanner: 'scanners/:id',
    'Scanner.start': 'scanners/:id/start',
    'Scanner.status': 'scanners/:id/status',
    'Scanner.stop': 'scanners/:id/stop',
    Session: 'sessions/:id',
    'Session.close': 'sessions/:id/close',
    'Session.filteredSearch': 'sessions/filteredSearch',
    'Session.itemStatus': 'sessions/:id/items/status',
    SessionMovement: 'sessionMovements/:id',
    'SessionMovement.create': 'sessionMovements/create',
    SessionItem: 'sessionItems/:id',
    ShippingEntry: 'shipping/report/entries/:id',
    ShippingParcel: 'shipping/report/parcels/:id',
    ShippingNotice: 'shipping/report/notices/:id',
    Stock: 'stock/:id',
    temporaryLogin: 'auth/temporaryLogin',
    'Transfer.movement': 'transfers/:id/movement',
    Transfer: 'transfers/:id',
    User: 'users/:id',
    'User.self': 'users/self',
    Xspan: 'xspans/:id',
    'Xspan.start': 'xspans/:id/start',
    'Xspan.status': 'xspans/:id/status',
    'Xspan.stop': 'xspans/:id/stop',
    Zone: 'zones/:id',
    'ZoneInventory.confirm': 'zoneInventories/:id/confirm',
    ZoneInventory: 'zoneInventories/:id',
    ShippingInbound: 'shippingInbounds/:id',
    ShippingOutbound: 'shippingOutbounds/:id',
    Info: 'info',
  },
]
